import {
  Order,
  MenuComboOrderItem,
  MenuFormulaOrderItem,
  OrderItem,
} from "@/redux/services/order/order.type";
import { Menu, MenuItem } from "@/redux/services/menu/menu.type";
import { MenuCombo } from "@/redux/services/menu/menu_combo.type";
import { MenuFormula } from "@/redux/services/menu/menu_formula.type";
import {
  comboSelectionKey,
  comboSelectionLabel,
  optionValueIdsFromRead,
  optionValueTitlesFromRead,
} from "@/lib/comboOptions";
import { CartEntry, FormulaCartSelection, buildCartEntryId } from "./types";

interface HydrationContext {
  menu?: Menu | null;
  combos?: MenuCombo[];
  formulas?: MenuFormula[];
}

/**
 * Walks a menu and returns the MenuItem matching the given id, regardless
 * of which section it lives in.
 */
function findMenuItemInMenu(
  menu: Menu | null | undefined,
  id: string,
): MenuItem | undefined {
  if (!menu) return undefined;
  for (const section of menu.menuSections ?? []) {
    const found = section.menuItems?.find((m) => m.id === id);
    if (found) return found;
  }
  return undefined;
}

function hydrateRegularItem(
  item: OrderItem,
  ctx: HydrationContext,
): CartEntry | null {
  const optionValueIds = item.selectedMenuItemOptionValues.map((v) => v.id);
  const optionLabels = item.selectedMenuItemOptionValues.map((v) => v.title);
  const unitPrice = item.quantity > 0 ? item.price / item.quantity : item.price;

  if (item.menuItem) {
    const fullItem =
      findMenuItemInMenu(ctx.menu, item.menuItem.id) ?? item.menuItem;
    return {
      id: buildCartEntryId(
        fullItem.id,
        "item",
        optionValueIds,
        item.additionalDemands ?? "",
      ),
      type: "item",
      menuItem: fullItem,
      name: fullItem.title,
      imageUrl: fullItem.image?.url,
      quantity: item.quantity,
      selectedOptionValueIds: optionValueIds,
      selectedOptionLabels: optionLabels,
      additionalDemands: item.additionalDemands ?? "",
      unitPrice,
      existingIri: item["@id"],
      originalQuantity: item.quantity,
    };
  }

  if (item.dailyDish?.id) {
    return {
      id: buildCartEntryId(
        item.dailyDish.id,
        "daily_dish",
        [],
        item.additionalDemands ?? "",
      ),
      type: "daily_dish",
      dailyDishId: item.dailyDish.id,
      name: item.dailyDish.name,
      imageUrl:
        typeof item.dailyDish.image === "object"
          ? item.dailyDish.image?.url
          : undefined,
      quantity: item.quantity,
      selectedOptionValueIds: [],
      selectedOptionLabels: [],
      additionalDemands: item.additionalDemands ?? "",
      unitPrice,
      existingIri: item["@id"],
      originalQuantity: item.quantity,
    };
  }

  return null;
}

function hydrateComboItem(
  item: MenuComboOrderItem,
  ctx: HydrationContext,
): CartEntry | null {
  const combo = item.menuCombo;
  if (!combo) return null;
  const fullCombo = ctx.combos?.find((c) => c.id === combo.id) ?? combo;
  const unitPrice = item.quantity > 0 ? item.price / item.quantity : item.price;

  const comboSelections = (item.selections ?? []).map((s) => {
    const selectedOptions = optionValueIdsFromRead(s.selectedOptions);
    return {
      menuItem: s.menuItem?.id ?? "",
      sideType: s.sideType,
      ...(selectedOptions.length > 0 ? { selectedOptions } : {}),
    };
  });

  const selectionLabels = (item.selections ?? []).map((s) =>
    s.menuItem
      ? comboSelectionLabel(
          s.menuItem.title,
          optionValueTitlesFromRead(s.selectedOptions),
        )
      : (s.sideType ?? ""),
  );

  // Match the create-flow's entry id derivation so identical edits dedupe
  const selectionKey = comboSelectionKey(comboSelections);

  return {
    id: buildCartEntryId(
      fullCombo.id,
      "combo",
      [],
      `${selectionKey}::${item.additionalDemands ?? ""}`,
    ),
    type: "combo",
    menuComboId: fullCombo.id,
    name: fullCombo.name,
    imageUrl: fullCombo.image?.url,
    quantity: item.quantity,
    selectedOptionValueIds: [],
    selectedOptionLabels: selectionLabels,
    additionalDemands: item.additionalDemands ?? "",
    unitPrice,
    comboSelections,
    existingIri: item["@id"],
    originalQuantity: item.quantity,
  };
}

function hydrateFormulaItem(
  item: MenuFormulaOrderItem,
  ctx: HydrationContext,
): CartEntry | null {
  const formula = item.menuFormula;
  if (!formula) return null;
  const fullFormula =
    ctx.formulas?.find((f) => f.id === formula.id) ?? formula;
  const unitPrice = item.quantity > 0 ? item.price / item.quantity : item.price;

  const formulaSelections: FormulaCartSelection[] = (item.selections ?? []).map(
    (s) => ({
      menuFormulaComponentItemId: s.menuFormulaComponentItem.id,
      menuItemId: s.menuItem?.id,
      selectedOptions: (s.selectedOptions ?? []).map((opt: any) =>
        typeof opt === "string" ? opt : opt?.id,
      ).filter(Boolean),
    }),
  );

  const selectionLabels = (item.selections ?? []).map(
    (s) =>
      s.menuItem?.title ??
      s.menuFormulaComponentItem.menuItem?.title ??
      s.menuFormulaComponentItem.name ??
      "",
  );

  const selectionKey = formulaSelections
    .map(
      (s) =>
        `${s.menuFormulaComponentItemId}:${(s.selectedOptions ?? []).slice().sort().join("+")}`,
    )
    .sort()
    .join(",");

  return {
    id: buildCartEntryId(
      fullFormula.id,
      "formula",
      [],
      `${selectionKey}::${item.additionalDemands ?? ""}`,
    ),
    type: "formula",
    menuFormulaId: fullFormula.id,
    name: fullFormula.name,
    imageUrl: fullFormula.image?.url,
    quantity: item.quantity,
    selectedOptionValueIds: [],
    selectedOptionLabels: selectionLabels,
    additionalDemands: item.additionalDemands ?? "",
    unitPrice,
    formulaSelections,
    existingIri: item["@id"],
    originalQuantity: item.quantity,
  };
}

export function cartFromOrder(
  order: Order,
  ctx: HydrationContext = {},
): CartEntry[] {
  const entries: CartEntry[] = [];

  for (const item of order.orderItems ?? []) {
    const entry = hydrateRegularItem(item, ctx);
    if (entry) entries.push(entry);
  }
  for (const item of order.menuComboOrderItems ?? []) {
    const entry = hydrateComboItem(item, ctx);
    if (entry) entries.push(entry);
  }
  for (const item of order.menuFormulaOrderItems ?? []) {
    const entry = hydrateFormulaItem(item, ctx);
    if (entry) entries.push(entry);
  }

  // Distinct order lines for the same product+options share a canonical entry
  // id (a committed order can carry several lines of the same item — each add-on
  // is its own line). Disambiguate collisions so per-line quantity/removal
  // targets the right line and React keys don't clash; the first occurrence
  // keeps the canonical id so adding more of that product still merges into it.
  const seenIds = new Set<string>();
  entries.forEach((entry, index) => {
    if (seenIds.has(entry.id)) {
      entry.id = `${entry.id}::line-${entry.existingIri ?? index}`;
    }
    seenIds.add(entry.id);
  });

  return entries;
}

export function notesFromOrder(order: Order): string {
  return typeof order.notes === "string" ? order.notes : "";
}
